/**
 * Credential-collection landing route (OL-63-links) — eagerly registers the
 * file route and re-applies its OWN guard; the page component and its
 * atom/RPC imports are deferred to `collect.$token.lazy.tsx` so they load only
 * with this route, never in the always-loaded `main` chunk.
 *
 * `orcaloops.ai/collect/<token>` rewrites onto `/orca/collect/$token` — every
 * orcaloops.ai path does, because `/orca` is a WILDCARD domain prefix. That is
 * also why this file lives under `routes/orca/` and NOT under a
 * `routes/orca_/` escape: a trailing-underscore escape registers the bare URL
 * `/collect/$token`, which the domain rewrite can never reach.
 *
 * Escaping the ADMIN gate while still requiring a SIGNED-IN person is the
 * `routes/clawinstall/admin_/resellers/route.tsx` pattern inverted: the layout
 * guard (`orcaLayoutBeforeLoad`) skips `requireAdmin` because
 * `/orca/collect` is in `ORCA_PUBLIC_ROOTS`, and this route re-applies
 * `requireAuth` in its place. The person handing over their own GitHub token or
 * Claude subscription is almost never an `admin`, and a link with no guard at
 * all would serve the form to anybody it was forwarded to.
 *
 * The session is necessary but not sufficient: the collect RPCs behind the page
 * compare `CurrentActor.person.id` to the link's `person_id` and answer
 * `not_yours` for everybody else, `admin` included.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 *
 * `requireAuth`, never `requireAdmin`.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const collectBeforeLoad = async () => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  return { auth };
};

/**
 * The document head: a title and `noindex` — a credential link is never a
 * search result — and the v5 frame's sheet, so the page paints styled.
 *
 * @since 1.0.0
 */
export const collectHead = () => ({
  meta: [{ title: "Connect a credential — Orcaloops" }, { name: "robots", content: "noindex" }],
  links: orcaloopsStylesheetLinks,
});

export const Route = createFileRoute("/orca/collect/$token")({
  beforeLoad: collectBeforeLoad,
  head: collectHead,
});
